import { useState, useEffect } from 'react';
import { X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useGetMapCategory, useUpdateCategory } from '@/services/mapdata/queries';
import { showToast } from '@/components/ui/show-toast';
import { useNavigate, useParams } from 'react-router-dom';
import PageContainer from '@/components/layout/page-container';
import { Heading } from '@/components/ui/heading';

export default function EditCategoryView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  // Get the specific category data
  const { data: categoriesData, isLoading: isLoadingCategory } = useGetMapCategory(
    id || '',
    {
      staleTime: 1000 * 60 * 2, // 2 minutes
      refetchOnWindowFocus: true,
      refetchOnMount: true
    }
  );
  
  const currentCategory = categoriesData;
  
  const updateCategoryMutation = useUpdateCategory(
    id || '',
    () => {
      showToast('success', 'Category updated successfully');
      navigate('/dashboard/data');
    },
    (error) => {
      showToast('error', 'Failed to update category');
      console.error('Update error:', error);
    }
  );

  // Initialize with proper typing
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [newItem, setNewItem] = useState<string>('');

  // Initialize data when category is loaded
  useEffect(() => {
    if (currentCategory) {
      setSelectedItems(currentCategory.tags || []);
    }
  }, [currentCategory]);

  const toggleItem = (item: string) => {
    setSelectedItems((prev) => 
      prev.includes(item)
        ? prev.filter((i) => i !== item)
        : [...prev, item]
    );
  };

  const handleInputChange = (value: string) => {
    setNewItem(value);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && newItem?.trim()) {
      e.preventDefault();
      const trimmedItem = newItem.trim();
      
      if (!selectedItems.includes(trimmedItem)) {
        setSelectedItems((prev) => [...prev, trimmedItem]);
      }
      setNewItem('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentCategory) {
      showToast('error', 'Category not found');
      return;
    }

    try {
      await updateCategoryMutation.mutateAsync({
        name: currentCategory.name,
        tags: selectedItems
      });
    } catch (error) {
      // Error is handled in the mutation callbacks
    }
  };

  if (isLoadingCategory) {
    return (
      <PageContainer scrollable={true}>
        <div className='flex h-64 items-center justify-center'>
          <p className='text-muted-foreground'>Loading category...</p>
        </div>
      </PageContainer>
    );
  }

  if (!currentCategory) {
    return (
      <PageContainer scrollable={true}>
        <div className='flex h-64 items-center justify-center'>
          <p className='text-muted-foreground'>Category not found</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer scrollable={true}>
      <div className='space-y-6 px-5'>
        {/* Header Section */}
        <div className='flex'>
          <Heading 
            title={`Edit ${currentCategory.name}`}
            description="Update the data items for this category"
          />
        </div>

        {/* Category Card */}
        <div className='md:max-w-5xl rounded-lg border bg-white p-6 shadow-sm'>
          <div className='mb-6 flex items-center gap-4'>
            <div className='rounded-md bg-primary p-3 text-primary-foreground'>
              <Check className='h-5 w-5' />
            </div>
            <div>
              <h2 className='text-lg font-medium text-primary'>
                {currentCategory.name}
              </h2>
              <p className='text-muted-foreground'>Modify data items for your map</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className='space-y-4'>
              {/* Selected Items Display */}
              <div className='rounded-md bg-muted/50 p-4'>
                <h3 className='mb-3 text-sm font-medium'>Current Data Items</h3>
                <div className='flex flex-wrap gap-2'>
                  {selectedItems.map((item) => (
                    <div
                      key={item}
                      className='flex items-center rounded-full border bg-background px-3 py-1.5 text-sm'
                    >
                      {item}
                      <button
                        type='button'
                        onClick={() => toggleItem(item)}
                        className='ml-2 text-muted-foreground hover:text-destructive'
                      >
                        <X className='h-4 w-4' />
                      </button>
                    </div>
                  ))}
                  {selectedItems.length === 0 && (
                    <p className="text-sm text-muted-foreground">No data items yet</p>
                  )}
                </div>
              </div>

              {/* Add New Item Input */}
              <div className='space-y-2'>
                <label className='text-sm font-medium'>Add New Data Item</label>
                <Input
                  placeholder='Type a new data item and press Enter'
                  value={newItem}
                  onChange={(e) => handleInputChange(e.target.value)}
                  onKeyDown={handleKeyPress}
                  className='bg-background'
                />
                <p className='text-xs text-muted-foreground'>
                  Press Enter to add the item to the list
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className='flex gap-3 pt-4 justify-between'>
              
              <Button
                type='button'
                variant='outline'
                onClick={() => navigate('/dashboard/data')}
              >
                Cancel
              </Button>
              <Button
                type='submit'
                disabled={updateCategoryMutation.isPending}
                className='bg-primary hover:bg-primary/90'
              >
                {updateCategoryMutation.isPending ? 'Updating...' : 'Update Category'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </PageContainer>
  );
}
